import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import StrategyService from '../../../../../api/services/strategies';
import { useVisualOverridesRecord } from '../../../../../hooks/useVisualOverrides';
import WebSocketManager from '../../../../../websocket/WebSocketManager';
import { useAuth } from '../../../../../contexts/AuthContext';
import { ChartSyncService } from '../../../../../components/SmartChart/utils';
import RDWatchDashboard from './RDWatchDashboard';
import { categorizeNodes, deriveLiveNodeOutputs, deriveLiveNodeInvalidReasons, keepIfSameReasons } from '../utils/nodeUtils';
import { FiZap } from 'react-icons/fi';

const PopOutDashboard = () => {
    const { strategyId } = useParams();
    const { isAuthenticated } = useAuth();
    //! 50-21 R10-T11 — ONE GET of the visual-overrides record per pop-out mount (read-only, zero PUTs);
    //! condition names resolve from it exactly as in the Builder dashboard.
    const { overridesSaved: visualOverrides } = useVisualOverridesRecord(strategyId);
    
    const [strategy, setStrategy] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Live Ticks 
    const [latestData, setLatestData] = useState({});
    
    // Interactive Data (Synced via BroadcastChannel)
    const [hoverData, setHoverData] = useState(null);
    
    // Live Execution Data (Calculated Backend Results)
    const [executionData, setExecutionData] = useState({});
    const [liveNodeOutputs, setLiveNodeOutputs] = useState({}); //* 142-37 loop 4
    const [liveNodeInvalidReasons, setLiveNodeInvalidReasons] = useState({}); //* 50-17
    
    // Dashboard State
    const [isLive, setIsLive] = useState(false); // Local toggle for Pop-out
    
    // 1. Load Strategy Config
    useEffect(() => {
        if (!strategyId) return;
        
        const loadStrategy = async () => {
            try {
                const response = await StrategyService.getStrategyById(strategyId);
                const s = response.data.data || response.data;
                setStrategy(s);
                
                let config = typeof s.config === 'string' ? JSON.parse(s.config) : (s.config || {});
                if (Array.isArray(config.nodes)) {
                    setNodes(config.nodes);
                }
                if (Array.isArray(config.edges)) {
                    setEdges(config.edges);
                }
            } catch (err) {
                console.error("[PopOut] Failed to load strategy", err);
            } finally {
                setLoading(false);
            }
        };
        loadStrategy();
    }, [strategyId]);

    // 2. Connect Sync Service (BroadcastChannel)
    useEffect(() => {
        if (!strategyId) return;
        
        const syncService = new ChartSyncService(strategyId);
        
        // 1. Request current graph structure from Parent (for unsaved changes)
        syncService.requestStructure();
        
        // 2. Listen for Structure Updates (Nodes/Edges)
        syncService.onStructure((payload) => {
            // console.log("[PopOut] Received structure update:", payload);
            if (payload.nodes) setNodes(payload.nodes);
            if (payload.edges) setEdges(payload.edges);
        });

        const unsubscribe = syncService.subscribe((param) => {
            // Received Crosshair Move from Main Window
            // Param contains enriched data payload
            if (param) {
                setHoverData(param);
            }
        });

        return () => {
            unsubscribe();
            syncService.destroy();
        };
    }, [strategyId]);

    // 3. Connect WebSocket (Live Data)
    useEffect(() => {
        if (!isAuthenticated || !strategy) return;
        
        const config = typeof strategy.config === 'string' ? JSON.parse(strategy.config) : (strategy.config || {});
        const instruments = config.instruments || (config.instrument ? [config.instrument] : []);
        const selectedInstrument = instruments[0];
        
        if (!selectedInstrument) return;
        
        const token = selectedInstrument.instrument_token || selectedInstrument.instrument_id;
        if (!token) return;
        
        const topic = `md:ticks:${token}`;
        const execTopic = `strategy:execution:${strategyId}`;
        
        const handleTick = (data) => {
            try {
                if (data) setLatestData(data);
            } catch (err) {
                console.error('[PopOut] Tick data parse error:', err);
            }
        };
        
        const handleExecutionUpdate = (msg) => {
            try {
                if (!msg?.node_results) return;
                
                // Use functional update to prevent  race conditions
                setExecutionData(prev => {
                    const flatData = {};
                    for (const [nodeId, res] of Object.entries(msg.node_results)) {
                        if (res?.value !== undefined) {
                            flatData[nodeId] = res.value;
                        }
                    }
                    return flatData;
                });
                //* 142-37 loop 4: the pop-out gets the same full-output map as the in-page dashboard
                setLiveNodeOutputs(deriveLiveNodeOutputs(msg.node_results));
                setLiveNodeInvalidReasons((prev) => keepIfSameReasons(prev, deriveLiveNodeInvalidReasons(msg.node_results)));
            } catch (err) {
                console.error('[PopOut] Execution data parse error:', err);
            }
        };

        const unsubscribeTick = WebSocketManager.subscribeTopic(topic, handleTick);
        const unsubscribeExec = WebSocketManager.subscribeTopic(execTopic, handleExecutionUpdate);
        
        return () => {
            unsubscribeTick();
            unsubscribeExec();
        };
    }, [isAuthenticated, strategy, strategyId]);

    // 4. Derive Layout
    const categorizedNodes = useMemo(() => {
          return categorizeNodes(nodes, edges);
    }, [nodes, edges]);

    // --> Identity fields mirrored from the Strategy Builder header (StrategyBuilder.jsx)
    const dataSourceNode = nodes.find(n => n.type === 'data_source');
    const dsLabel = dataSourceNode?.data?.label || 'N/A';
    const timeframe = dataSourceNode?.data?.timeframe || '5m';
    const logicCount = nodes.filter(n => ['indicator', 'math', 'comparator', 'logic_gate'].includes(n.type)).length;
    const signalCount = nodes.filter(n => ['signal', 'position_trigger'].includes(n.type)).length;
    const statusVariant = { draft: 'neutral', backtested: 'accent', paper: 'info', live: 'profit', stopped: 'warn', archived: 'loss', backtesting: 'info' }[strategy?.status] || 'neutral';
    const statusLabel = { draft: 'Draft', backtested: 'Backtested', paper: 'Paper Trade', live: 'Live Trade', stopped: 'Stopped', archived: 'Archived', backtesting: 'Backtesting' }[strategy?.status] || 'Draft';
    const statusPulses = ['live', 'paper', 'backtesting'].includes(strategy?.status);
    const versionLabel = String(strategy?.version || 'v1.0').startsWith('v') ? String(strategy?.version || 'v1.0') : `v${strategy?.version || '1.0'}`;

    if (loading) return <div className="flex items-center justify-center h-screen bg-bg-1 text-fg-3 text-sm font-medium">Loading Dashboard…</div>;

    return (
        <div className="h-screen w-screen bg-bg-1 overflow-hidden flex flex-col">
            {/* Title strip — mirrors the Strategy Builder identity header, on the DS */}
            <div className="shrink-0 flex items-center justify-between gap-4 px-4 h-14 bg-bg-2 border-b border-[var(--divider)]">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center bg-[color-mix(in_oklch,var(--color-accent)_16%,transparent)] text-[var(--color-accent)]">
                        <FiZap size={20} />
                    </div>
                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-lg font-bold text-fg-1 tracking-tight truncate">{strategy?.name || 'Untitled Strategy'}</span>
                            <span className={`ds-badge xs ${statusVariant} font-bold uppercase tracking-wider shrink-0`}>
                                <span className={`ds-badge-dot ${statusPulses ? 'pulse' : ''}`} />
                                {statusLabel}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-fg-3 font-medium min-w-0">
                            <span className="shrink-0 flex items-center bg-bg-3 px-1.5 rounded text-[10px] font-mono border border-[var(--divider)]">{versionLabel}</span>
                            {strategy?.description
                                ? <span className="truncate max-w-[280px]" title={strategy.description}>{strategy.description}</span>
                                : <span className="opacity-50 whitespace-nowrap">No description</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-1.5">
                        <span className="ds-badge xs accent mono">{dsLabel}</span>
                        <span className="ds-badge xs neutral mono">{timeframe}</span>
                    </div>
                    {(logicCount > 0 || signalCount > 0) && (
                        <span className="hidden md:inline text-[11px] font-mono text-fg-3 whitespace-nowrap">{logicCount} Nodes · {signalCount} Signals</span>
                    )}
                    <div className="flex items-center gap-1.5 border-l border-[var(--divider)] pl-3">
                        <span className={`w-2 h-2 rounded-full ${hoverData ? 'bg-[var(--color-profit)] animate-pulse' : 'bg-fg-3'}`}></span>
                        <span className="text-xs text-fg-3 font-medium">Synced</span>
                    </div>
                </div>
            </div>
            <div className="flex-1 overflow-hidden relative">
                <RDWatchDashboard 
                    categorizedNodes={categorizedNodes}
                    latestData={latestData}
                    executionData={executionData}
                    liveNodeOutputs={liveNodeOutputs}
                    liveNodeInvalidReasons={liveNodeInvalidReasons}
                    hoverData={hoverData}
                    isLive={isLive}
                    onToggleLive={() => setIsLive(!isLive)}
                    onPopOut={() => window.close()} // Close on click
                    onItemClick={() => {}} // No chart interaction available here
                    selectedNodeId={null}
                    activeChartNodeId={null}
                    primaryTimeframe={nodes.find(n => n.type === 'data_source')?.data?.timeframe || '5m'}
                    nodes={nodes}
                    edges={edges}
                    visualOverrides={visualOverrides}
                />
            </div>
        </div>
    );
};

export default PopOutDashboard;
